import { HttpResponse } from "@capacitor/core";
import {
  IonButton,
  IonRadio,
  IonRadioGroup,
  IonText,
  IonTextarea,
} from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import { useHistory } from "react-router";
import { Match, MatchMeta, MatchReport } from "../../data/Match";
import { MatchesResponse } from "../../data/MatchesResponse";
import { JIBI_BASE_URL, POST } from "../../util/ApiClient";
import { UserContext } from "../../util/BetterDatesApp";
import { handleError } from "../../util/error";
import { ModalStateType } from "./Modal";

export default function ReportModal({
  setModalState,
  match,
}: ReportModalProps) {
  const userContext = useContext(UserContext);
  const history = useHistory();
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherText, setOtherText] = useState<string>("");
  const [inProgress, setInProgress] = useState(false);
  const queryClient = useQueryClient();

  if (!userContext?.userState.loggedIn) {
    return <></>;
  }
  const savedUser = userContext.userState.savedUser;

  const handleRadioChange = (value: string) => {
    setSelectedReason(value);
    if (value !== "Others") {
      setOtherText("");
    }
  };

  return (
    <main className="flex h-full w-full flex-col justify-between px-4 py-6">
      <div className="flex h-full w-full flex-col pb-4">
        <IonRadioGroup
          value={selectedReason}
          onIonChange={(e) => handleRadioChange(e.detail.value)}
        >
          <div className="scrollable scrollable flex h-full w-full flex-col gap-8 overflow-y-scroll no-scrollbar">
            <div className="flex items-center">
              <IonRadio
                value="Spam"
                color="dark"
                labelPlacement="end"
                className="w-full"
                justify="start"
              >
                <IonText className="h-8 w-full">Spam</IonText>
              </IonRadio>
            </div>
            <div className="flex items-center">
              <IonRadio
                value="Inappropriate"
                color="dark"
                labelPlacement="end"
                className="w-full"
                justify="start"
              >
                <IonText className="h-8 w-full">Inappropriate content</IonText>
              </IonRadio>
            </div>
            <div className="flex items-center">
              <IonRadio
                value="Harassment"
                color="dark"
                labelPlacement="end"
                className="w-full"
                justify="start"
              >
                <IonText className="h-8 w-full">Harassment</IonText>
              </IonRadio>
            </div>
            <div className="flex items-center">
              <IonRadio
                value="Others"
                color="dark"
                labelPlacement="end"
                className="w-full"
                justify="start"
              >
                <IonText className="h-8 w-full">Other</IonText>
              </IonRadio>
            </div>
          </div>
        </IonRadioGroup>
        {selectedReason === "Others" && (
          <IonTextarea
            ref={(ref) => {
              if (ref) {
                ref.getInputElement().then((element) => {
                  element.dir = "auto";
                });
              }
            }}
            autofocus={true}
            color={"dark"}
            className="force-dark mt-4 justify-center overflow-y-scroll rounded border border-black px-4 no-scrollbar"
            placeholder="Please specify"
            value={otherText}
            onIonInput={(e) => setOtherText(e.detail.value!)}
          />
        )}
      </div>
      <div className="flex w-full flex-row justify-end">
        <IonButton
          className={"mt-8 h-9 w-20"}
          shape={"round"}
          color={"dark"}
          size={"small"}
          disabled={selectedReason.length === 0 || inProgress}
          onClick={(e) => {
            e.preventDefault();
            setInProgress(true);

            const reporter = savedUser?.user.id;
            const reported = match.id;
            const token = savedUser?.token;
            const reason = selectedReason;
            const description = otherText;

            sendReport(reporter, reported, match.id, reason, description, token)
              .then((response) => {
                if (response.status !== 201) {
                  return Promise.reject(response);
                }

                const reported = response.data.reported as MatchReport;
                const updatedMeta: MatchMeta = {
                  ...match.meta,
                  reported: reported,
                };
                const updatedMatch: Match = {
                  ...match,
                  meta: updatedMeta,
                };
                const updatedMatches =
                  queryClient
                    .getQueryData<MatchesResponse>([
                      userContext.queryKeys.matches,
                    ])
                    ?.matches?.map((value) => {
                      return value.id === updatedMatch.id
                        ? updatedMatch
                        : value;
                    }) || [];
                queryClient.setQueryData<MatchesResponse>(
                  [userContext.queryKeys.matches],
                  { matches: updatedMatches },
                );

                setModalState({ isVisible: false });
                history.replace("/");
              })
              .catch((error) => handleError(error, userContext))
              .finally(() => {
                setInProgress(false);
              });
          }}
        >
          Submit
        </IonButton>
      </div>
    </main>
  );
}

function sendReport(
  reporter: string,
  reported: string,
  matchId: string,
  reason: string,
  description: string,
  token: string,
): Promise<HttpResponse> {
  return POST({
    url: `${JIBI_BASE_URL}/v1/match/${matchId}/report`,
    headers: {
      Authorization: token,
    },
    body: {
      reporter: reporter,
      reported: reported,
      reason: reason,
      description: description,
    },
  });
}

interface ReportModalProps {
  setModalState: React.Dispatch<React.SetStateAction<ModalStateType>>;
  match: Match;
}
